import { OfferLetterFormsService } from 'api-clients/monolith/services/OfferLetterFormsService';
import { OfferLettersService } from 'api-clients/monolith/services/OfferLettersService';
import { toApprovalPlan } from 'containers/ApprovalRules/ApprovalsQueue/normalize';
import { Signer } from 'containers/OfferLetterTemplates/types';

import { readTemplateOffer } from './documents';
import { isEmittable } from './SignaturePlacement/geometry';
import {
  PLACEMENT_FIELD_TYPES,
  PlacementFieldType,
  SignatureFieldPlacement,
} from './SignaturePlacement/types';
import {
  ApprovalState,
  ApproverGroup,
  ConditionalBlock,
  ConditionOperator,
  EditedKeys,
  OfferLetterFormData,
  OfferPreviewData,
  OfferVersion,
  PreviewResult,
  TypedInput,
  VisibilityCondition,
  WriteBackSource,
} from './types';

type RawForm = Awaited<
  ReturnType<typeof OfferLetterFormsService.getInternalApiApplicantsOfferLetterForm>
>;
type RawPreview = Awaited<
  ReturnType<typeof OfferLetterFormsService.postInternalApiApplicantsOfferLetterFormPreview>
>;

// The generated client types every field optional; normalize to the strict
// view the UI works with (empty/false/null defaults).
const normalizeApproval = (
  approval: RawForm['approval'] | RawPreview['approval'],
): ApprovalState => ({
  required: approval?.required ?? false,
  triggered_rules: (approval?.triggered_rules ?? []).map(rule => ({
    label: rule.label ?? '',
    description: rule.description ?? '',
    field_key: rule.field_key ?? null,
    conditional: rule.conditional,
  })),
});

// AXHE-4187. `visible_when` on a field and `included_when` on a block are the
// same rule shape, so they normalize through one function — a second copy is a
// place for the two to drift on what a missing operator falls back to.
const normalizeCondition = (condition: {
  data_key?: string | null;
  operator?: ConditionOperator | null;
  value?: string | null;
  values?: (string | null)[] | null;
}): VisibilityCondition => ({
  data_key: condition.data_key ?? '',
  operator: condition.operator ?? 'equals',
  value: condition.value ?? null,
  values: Array.isArray(condition.values)
    ? condition.values.filter((entry): entry is string => typeof entry === 'string')
    : null,
});

interface RawTypedInput {
  key_type?: string | null;
  options?: { value?: string | null; label?: string | null }[] | null;
  written_back?: boolean | null;
  source?: WriteBackSource | null;
  source_value?: string | null;
}

// HIR-375. Every property defaults to null — what a field mapped to nothing
// already carries — so a payload from an older backend renders as before.
const normalizeTypedInput = (field: RawTypedInput): TypedInput => ({
  key_type: field.key_type ?? null,
  options: field.options
    ? field.options.map(option => ({
        value: option.value ?? '',
        label: option.label || option.value || '',
      }))
    : null,
  written_back: field.written_back ?? null,
  source: field.source ?? null,
  source_value: field.source_value ?? null,
});

// Shared by the form definition and the preview response, which return the
// same block shape on purpose (the controller builds both from one call).
const normalizeBlock = (block: {
  key?: string | null;
  label?: string | null;
  included?: boolean | null;
  included_when?: Parameters<typeof normalizeCondition>[0][] | null;
}): ConditionalBlock => ({
  key: block.key ?? '',
  label: block.label ?? null,
  included: block.included ?? false,
  // Defaults to [] — which for a BLOCK means EXCLUDED, not included, matching
  // the server's default for a block nobody configured. A backend that predates
  // this field would therefore look like "every section excluded", but that
  // cannot hide anything: such a payload also carries no `block_occurrences`,
  // and a field with no occurrences is never withdrawn. The two defaults are
  // safe together, which is the point of ANDing them.
  included_when: (block.included_when ?? []).map(normalizeCondition),
});

// An absent report reads as one naming nothing, which is what the offer stores
// before any client reports.
const normalizeEditedKeys = (
  raw: { field_values?: string[] | null; condition_values?: string[] | null } | null | undefined,
): EditedKeys => ({
  field_values: raw?.field_values ?? [],
  condition_values: raw?.condition_values ?? [],
});

export const normalizeForm = (raw: RawForm): OfferLetterFormData => ({
  offer_letter_template: raw.offer_letter_template
    ? {
        id: raw.offer_letter_template.id ?? '',
        title: raw.offer_letter_template.title ?? '',
      }
    : null,
  available_templates: (raw.available_templates ?? [])
    .filter((template): template is NonNullable<typeof template> => template != null)
    .map(template => ({
      id: template.id ?? '',
      title: template.title ?? '',
      // The per-template offer state driving the document list (AXHE-4181).
      // `readTemplateOffer` is the single reader of that payload — see documents.ts.
      offer: readTemplateOffer(template),
    })),
  fields: (raw.fields ?? []).map(field => ({
    ...normalizeTypedInput(field),
    key: field.key ?? '',
    label: field.label ?? null,
    kind: field.kind ?? 'manual_entry',
    data_type: field.data_type ?? 'text',
    currency_format: field.currency_format ?? false,
    spell_out: field.spell_out ?? false,
    // `formulas` is `jsonb not null` with no array-shape validation on the
    // model, so a stray migration or a hand-written console edit can leave it a
    // non-array value OR an array with a non-string member. Guard both: without
    // the member filter, a value like `[{}]` survives here and reaches
    // `formula.ts`'s `parseFormula`, where `normalizeFormulaOperators(...).trim()`
    // calls `.split`/`.trim` on the object and crashes Send Offer for every
    // recruiter opening that template — the same guard VariableRow.tsx applies
    // on the authoring side.
    formulas: Array.isArray(field.formulas)
      ? field.formulas.filter((formula): formula is string => typeof formula === 'string')
      : [],
    editable: field.editable ?? true,
    prefilled: field.prefilled ?? false,
    value: field.value ?? '',
    data_key: field.data_key ?? null,
    // Same null-vs-[] rule as a condition field's below — see the comment there
    // (AXHE-4180).
    allowed_values: field.allowed_values ?? null,
    // AXHE-4187. A field the server didn't report on is one with no conditions
    // — always asked — so both default to "shown, unconditionally". That is
    // also the shape every payload had before this ticket, so an older backend
    // renders exactly as it did.
    visible: field.visible ?? true,
    visible_when: (field.visible_when ?? []).map(normalizeCondition),
    // AXHE-4187, the section half. [] means "no occurrences", which the section
    // rule reads as "cannot withdraw this field" — so a payload from a backend
    // that predates this key behaves exactly as it did.
    block_occurrences: (field.block_occurrences ?? []).map(chain => [...(chain ?? [])]),
  })),
  condition_fields: (raw.condition_fields ?? []).map(field => ({
    ...normalizeTypedInput(field),
    key: field.key ?? '',
    label: field.label ?? null,
    kind: 'condition' as const,
    // null and [] mean different things here and must NOT be collapsed: null is
    // "these conditions can't be enumerated, render a free input", while an
    // empty array would render a select with nothing to pick. The server never
    // sends [], but the generated client types it optional, so a missing value
    // has to fall to null rather than to [].
    allowed_values: field.allowed_values ?? null,
    editable: field.editable ?? true,
    prefilled: field.prefilled ?? false,
    value: field.value ?? '',
  })),
  conditional_blocks: (raw.conditional_blocks ?? []).map(normalizeBlock),
  approval: normalizeApproval(raw.approval),
  edited_keys: normalizeEditedKeys(raw.edited_keys),
});

export const normalizePreview = (raw: RawPreview): PreviewResult => ({
  approval: normalizeApproval(raw.approval),
  missing_fields: (raw.missing_fields ?? []).map(missing => ({
    key: missing.key ?? '',
    reason: missing.reason ?? 'missing_value',
  })),
  can_generate: raw.can_generate ?? false,
  conditional_blocks: (raw.conditional_blocks ?? []).map(normalizeBlock),
});

type RawOffer = Awaited<
  ReturnType<typeof OfferLettersService.postInternalApiApplicantsOfferLetters>
>;

const PLACEMENT_TYPES = new Set<string>(PLACEMENT_FIELD_TYPES);

/**
 * Coerce the stored `signature_field_placements` jsonb into the editor's view
 * (AXHE-4085).
 *
 * The column is schemaless, so a row written by an older client — or by a
 * console fix during an incident — can be any shape at all. A malformed row is
 * dropped rather than repaired: a field the recruiter can neither see nor
 * correct is worse than one that is missing from the editor, and the same
 * validation on the backend would reject it at send time anyway.
 */
export const normalizePlacements = (raw: unknown): SignatureFieldPlacement[] => {
  if (!Array.isArray(raw)) return [];

  return raw
    .filter(
      (row): row is Record<string, unknown> =>
        typeof row === 'object' && row !== null,
    )
    .filter(row => PLACEMENT_TYPES.has(String(row.type)))
    .map(row => ({
      type: row.type as PlacementFieldType,
      signer: Number(row.signer) || 0,
      page: Number(row.page) || 0,
      x: Number(row.x) || 0,
      y: Number(row.y) || 0,
      width: Number(row.width) || 0,
      height: Number(row.height) || 0,
      // Absent means required, matching FormFieldsBuilder's `fetch(:required, true)`.
      required: row.required !== false,
    }))
    .filter(isEmittable);
};

export const normalizeOfferPreview = (raw: RawOffer): OfferPreviewData => {
  const groups: ApproverGroup[] = (raw.approval?.groups ?? []).map(group => ({
    rule: {
      label: group.rule?.label ?? '',
      description: group.rule?.description ?? '',
      field_key: group.rule?.field_key ?? null,
    },
    approvers: (group.approvers ?? []).map(approver => ({
      name: approver.name ?? '',
      email: approver.email ?? '',
    })),
  }));

  // `signers` is the only key of this payload the offer preview schema does not
  // describe, so it is read off a widened view rather than by hand-editing
  // api-clients/, which is generated. Every other key comes off `raw`, whose
  // type IS the generated client: a field renamed on the server drops out of
  // that type the moment `rake rswag:generate` runs, and this file stops
  // compiling. Widening a key the spec already carries would trade that build
  // failure for a silent `undefined` and, through the defaults below, a control
  // that disappears from the rail with nothing to say why. So widen only what
  // the spec genuinely omits.
  const extended = raw as RawOffer & {
    signers?: Partial<Signer>[];
  };

  return {
    id: raw.id ?? '',
    status: raw.status ?? 'draft',
    // The offer preview schema types contract_type as a plain string; it's one
    // of the known contract types at runtime (server-sourced).
    contract_type: (raw.contract_type ?? null) as OfferPreviewData['contract_type'],
    field_values: raw.field_values ?? {},
    condition_values: raw.condition_values ?? {},
    edited_keys: normalizeEditedKeys(raw.edited_keys),
    rendered_pdf_url: raw.rendered_pdf_url ?? null,
    approval: {
      required: raw.approval?.required ?? false,
      request_id: raw.approval?.request_id ?? null,
      // Defaults to null on its own rather than to `request_id`: inferring the
      // newest request from the open one would claim a row exists on every
      // offer whose request has since been decided.
      latest_request_id: raw.approval?.latest_request_id ?? null,
      notified_approver_count: raw.approval?.notified_approver_count ?? 0,
      groups,
      plan: toApprovalPlan(raw.approval?.plan),
    },
    cta: raw.cta ?? 'send_to_candidate',
    source: (raw.source ?? 'generated') as OfferPreviewData['source'],
    upload_override_enabled: raw.upload_override_enabled ?? false,
    // Defaults to false, which hides the revert control. That is the safe
    // default in both directions: against a backend that predates AXHE-4266 the
    // endpoint doesn't exist, and against one that does, a payload we couldn't
    // read the flag from is not evidence the revert would be accepted. A missing
    // control is recoverable — the recruiter can still re-upload — where one
    // that 422s on press is not (AXHE-4285).
    can_revert_to_template: raw.can_revert_to_template ?? false,
    // Same safe default as can_revert_to_template, for the same reasons: a
    // payload the flag can't be read from is not evidence a cancel would be
    // accepted (AXHE-4682).
    can_cancel: raw.can_cancel ?? false,
    // Same safe default again (AXHE-4425). An Edit control that 422s on press
    // is worse than none: the recruiter reads the refusal as the offer being
    // broken, where a missing control leaves Cancel offer as the way forward.
    can_revise: raw.can_revise ?? false,
    // Same safe default: a discard the server refuses is worse than none.
    can_discard_edit: raw.can_discard_edit ?? false,
    revision: raw.revision ?? false,
    // Null against a backend that predates the field, which keeps the
    // confirmation's plain sentence for a sent offer.
    open_revision: raw.open_revision?.status ? { status: raw.open_revision.status } : null,
    // Rows missing an id are dropped: the id is what Download addresses, so a
    // row without one is a version the panel can name and cannot produce.
    versions: (raw.versions ?? [])
      .filter((version): version is { id: string } & OfferVersion => Boolean(version?.id))
      .map(version => ({
        id: version.id,
        sent_at: version.sent_at ?? null,
        sent_by: version.sent_by ?? null,
        // Carried through undefined rather than defaulted to false.
        // `splitVersions` reads an absent value as "this backend does not report
        // retirement" and falls back to the positional rule. A `?? false` default
        // here would instead tell it every row is still live.
        retired: version.retired,
      })),
    signature_field_placements: normalizePlacements(raw.signature_field_placements),
    signers: extended.signers ?? [],
  };
};
